import { Plus, Search, ShieldCheck, UserSearch } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  Pagination,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useDisclosuresToday,
  usePatients,
  usePossibleDuplicates,
} from '@/features/patients/api';
import {
  CHECK_LABELS,
  MEMBER_PRICE_LABELS,
  REQUESTER_LABELS,
  TOPIC_LABELS,
  type DuplicatePair,
  type PatientSummary,
} from '@/features/patients/types';
import { lastVisitLabel } from '@/features/patients/format';
import { PageContainer } from '@/layouts/PageContainer';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import { CounterEkycDrawer } from '@/pages/queue/CounterEkycDrawer';
import { DisclosureDrawer } from './DisclosureDrawer';
import { MergeDrawer } from './MergeDrawer';
import { PatientDetailsDrawer } from './PatientDetailsDrawer';
import { PatientIdentityText } from './PatientIdentityText';
import styles from './patients.module.css';

const PAGE_SIZE = 20;
/** Live search: wait for a pause in typing, from 2 characters (UAT RL-705). */
const SEARCH_DEBOUNCE_MS = 400;

const REASONS: Record<DuplicatePair['reason'], string> = {
  same_document: 'Same document number',
  same_name_and_birth_date: 'Same name and date of birth',
  same_phone_and_first_name: 'Same phone and first name',
};

/**
 * Admin v2 "Patients" (#24): possible duplicate records, the patient list with
 * coverage, member price and eKYC, and a log of information released today.
 */
export function PatientsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const [draft, setDraft] = useState(q);
  const [drawerId, setDrawerId] = useOptionalUrlState('drawer');
  const [ekycId, setEkycId] = useOptionalUrlState('ekyc');
  const [mergeId, setMergeId] = useOptionalUrlState('merge');
  const [disclosure, setDisclosure] = useUrlState<'closed' | 'open'>(
    'disclosure',
    'closed',
    ['closed', 'open'],
  );
  const { hasPermission } = useSession();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = usePatients({
    q: q || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const dups = usePossibleDuplicates();
  const released = useDisclosuresToday();
  const ekycFor = data?.data.find((patient) => patient.id === ekycId) ?? null;
  const mergePair =
    dups.data?.find(({ a, b }) => `${a.id}:${b.id}` === mergeId) ?? null;
  const asking = disclosure === 'open';

  // Debounced live search; Enter and the button still search at once.
  useEffect(() => {
    const term = draft.trim();
    if (term === q || (term.length > 0 && term.length < 2)) return;
    const t = setTimeout(() => {
      const next = new URLSearchParams();
      if (term) next.set('q', term);
      setParams(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, q, setParams]);

  function onSearch(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    if (draft.trim()) next.set('q', draft.trim());
    setParams(next);
  }

  const columns: Column<PatientSummary>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (p) => (
        <div className={styles.patientCell}>
          <span className={styles.avatarSm} aria-hidden="true">
            {p.fullName[0]?.toUpperCase()}
          </span>
          <div>
            <p className={styles.name}>{p.fullName}</p>
            <p className={styles.sub}>
              <PatientIdentityText identity={p.identity} />
            </p>
          </div>
        </div>
      ),
    },
    { key: 'phone', header: 'Phone', render: (p) => p.phone ?? '—' },
    {
      key: 'coverage',
      header: 'Coverage',
      render: (p) =>
        p.coverage ? `${p.coverage.payer.name} ${p.coverage.memberNo}` : 'Self',
    },
    {
      key: 'member',
      header: 'Member price',
      render: (p) =>
        p.memberPriceStatus === 'none' ? (
          '-'
        ) : (
          <Badge
            tone={p.memberPriceStatus === 'active' ? 'success' : 'warning'}
          >
            {MEMBER_PRICE_LABELS[p.memberPriceStatus]}
          </Badge>
        ),
    },
    {
      key: 'ekyc',
      header: 'eKYC',
      render: (p) => (
        <Badge tone={p.ekycStatus === 'verified' ? 'success' : 'warning'}>
          {p.ekycStatus === 'verified' ? 'Verified' : 'Unverified'}
        </Badge>
      ),
    },
    {
      key: 'last',
      header: 'Last visit',
      render: (p) =>
        p.status === 'merged' ? 'Merged' : lastVisitLabel(p.lastVisitAt),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (p) => (
        <div className={styles.actions}>
          {p.ekycStatus !== 'verified' &&
            p.status === 'active' &&
            hasPermission('patients:update') && (
              <Button
                size="sm"
                icon={<ShieldCheck size={14} />}
                onClick={() => setEkycId(p.id)}
              >
                Verify eKYC
              </Button>
            )}
          <Button size="sm" onClick={() => setDrawerId(p.id)}>
            Details
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/patients/${p.id}`)}
          >
            Record
          </Button>
        </div>
      ),
    },
  ];

  const meta = data?.meta;
  const pairs = dups.data ?? [];
  return (
    <PageContainer>
      <Card
        title="Possible duplicate records"
        actions={
          <span className={styles.count}>
            Records are merged after review, never deleted
          </span>
        }
      >
        {pairs.length === 0 && (
          <p className={styles.sub}>No possible duplicates found.</p>
        )}
        <ul className={styles.dupList}>
          {pairs.map((d) => (
            <li key={`${d.a.id}:${d.b.id}`}>
              <div>
                <p>
                  <strong>{d.a.fullName}</strong>{' '}
                  <PatientIdentityText identity={d.a.identity} /> and{' '}
                  <strong>{d.b.fullName}</strong>{' '}
                  <PatientIdentityText identity={d.b.identity} />
                </p>
                <p className={styles.sub}>
                  {REASONS[d.reason]} · second record from{' '}
                  {new Date(d.b.registeredAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
              {hasPermission('patients:merge') ? (
                <Button
                  size="sm"
                  onClick={() => setMergeId(`${d.a.id}:${d.b.id}`)}
                >
                  Review
                </Button>
              ) : (
                <Badge tone="warning">Clinic manager to review</Badge>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="Patients"
        actions={
          <>
            <span className={styles.count}>
              Keyed by MyKad
              {meta
                ? ` · ${data?.data.length ?? 0} shown of ${meta.total}`
                : ''}
            </span>
            <Button
              size="sm"
              icon={<UserSearch size={14} />}
              onClick={() => setDisclosure('open')}
            >
              Someone is asking about a patient
            </Button>
            <Link to="/patients/deleted-app-accounts">
              <Button size="sm">Deleted app accounts</Button>
            </Link>
            {hasPermission('patients:register') && (
              <Link to="/patients/register">
                <Button size="sm" variant="primary" icon={<Plus size={14} />}>
                  Register patient
                </Button>
              </Link>
            )}
          </>
        }
        flush
      >
        <form className={styles.searchBar} onSubmit={onSearch} role="search">
          <Input
            aria-label="Search patients"
            placeholder="Name, MyKad, passport or phone"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button type="submit" icon={<Search size={14} />}>
            Search
          </Button>
        </form>
        <DataTable
          caption="Patients"
          columns={columns}
          rows={data?.data}
          rowKey={(p) => p.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title={q ? 'No patient matches this search' : 'No patients yet'}
              body={
                q
                  ? 'Check the spelling or search by MyKad, passport or phone.'
                  : 'Registered patients appear here.'
              }
            />
          }
          footer={
            meta && meta.total > meta.pageSize ? (
              <Pagination
                meta={meta}
                onPageChange={(next) => {
                  const p = new URLSearchParams(params);
                  p.set('page', String(next));
                  setParams(p);
                }}
              />
            ) : undefined
          }
        />
      </Card>

      <Card title="Information released today">
        {(released.data?.entries.length ?? 0) === 0 && (
          <p className={styles.sub}>Nothing asked about today.</p>
        )}
        <ul className={styles.dupList}>
          {released.data?.entries.map((e) => (
            <li key={e.id}>
              <div>
                <p>
                  <strong>{e.patient.fullName}</strong> ·{' '}
                  {REQUESTER_LABELS[e.requester]}
                  {e.requesterName ? ` (${e.requesterName})` : ''} ·{' '}
                  {TOPIC_LABELS[e.topic]}
                </p>
                <p className={styles.sub}>
                  {new Date(e.recordedAt).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {CHECK_LABELS[e.identityCheck]} · {e.recordedBy.fullName}
                </p>
              </div>
              <Badge
                tone={
                  e.outcome === 'released'
                    ? 'success'
                    : e.outcome === 'referred'
                      ? 'warning'
                      : 'danger'
                }
              >
                {e.outcome === 'released'
                  ? 'Released'
                  : e.outcome === 'referred'
                    ? 'Referred to manager'
                    : 'Nothing released'}
              </Badge>
            </li>
          ))}
        </ul>
      </Card>

      <PatientDetailsDrawer
        patientId={drawerId}
        onClose={() => setDrawerId(null)}
      />
      <CounterEkycDrawer patient={ekycFor} onClose={() => setEkycId(null)} />
      <MergeDrawer pair={mergePair} onClose={() => setMergeId(null)} />
      <DisclosureDrawer open={asking} onClose={() => setDisclosure('closed')} />
    </PageContainer>
  );
}
